import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../services/api'; 

const InitiateVisitPage = () => {
  // Define the initial state for the visit data
  const initialVisitData = {
    CoreServer_CaseID: ``, // Generate a unique ID
    patient_info: {
      patient_id: '',
      patient_uuid: '',
      novu_subscriber_id: '', // Novu subscriber ID (same as patient_uuid in our architecture)
      first_name: '',
      last_name: '',
      dob: '', // YYYY-MM-DD format
      email: '', 
      phone: '', 
      locale: 'en-US', // Default to English for testing
      timezone: 'America/Chicago', // Default to America/Chicago for testing
    },
    provider_info: {
      id: '',
      provider_id: '',
      provider_uuid: '',
      novu_subscriber_id: '', // Novu subscriber ID (same as provider_uuid in our architecture)
      first_name: '',
      middle_name: '',
      last_name: '',
      specialties: [],
      provider_type: '',
      phone: '',
      phone_2: '',
      email: '',
      created_at: '',
      updated_at: '',
      is_active: true,
      health_system: null,
      timezone: 'America/New York', // Default timezone for testing
    },
    healthcare_info: {
      health_system_id: '',
      name: '',
    },
    initial_forms_data: [], // Will be populated with form data entries
    initial_insurance_data: {
      insurance_provider: '', 
      member_id: '', 
      group_number: '', 
      front_image_path: '', 
      back_image_path: '', 
      verification_status: 'pending', // 
    },
    appointment_info: {
      ehr_appointment_id: '',
      ehr_encounter_id: '',
      ehr_system: '',
      appointment_type: '',
      appointment_type_ehr_code: '',
      start_datetime: '',
      end_datetime: '',
      minutes_duration: '',
      timezone: 'America/Chicago',
      service_category: '',
      service_type: '',
      specialty_text: '',
      reason: '',
      priority: 'routine',
      department_name: '',
      department_ehr_id: '',
      clinic_name: '',
      clinic_ehr_id: '',
      description: '',
      comments: '',
      notes: '',
      patient_instructions: '',
      confirmation_status: 'unconfirmed',
      copay_amount: '',
      authorization_required: false,
      follow_up_required: false,
      // Fields that go into other_info
      room: '',
      floor: '',
      building: '',
      provider_name: '',
      provider_npi: '',
      provider_ehr_id: '',
      custom_fields: {}
    },
    include_zoom_session: false, // Boolean toggle for Zoom session
  };
  
  const [visitData, setVisitData] = useState(initialVisitData);
const [copySuccessMap, setCopySuccessMap] = useState({}); // State for copy success messages

  const handleCopy = async (text, identifier) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccessMap(prev => ({ ...prev, [identifier]: 'Copied!' }));
      setTimeout(() => {
        setCopySuccessMap(prev => {
          const newState = { ...prev };
          delete newState[identifier];
          return newState;
        });
      }, 2000); // Clear message after 2 seconds
    }).catch(err => {
      setCopySuccessMap(prev => ({ ...prev, [identifier]: 'Failed to copy!' }));
      setTimeout(() => {
        setCopySuccessMap(prev => {
          const newState = { ...prev };
          delete newState[identifier];
          return newState;
        });
      }, 2000);
    });
  };

  const [formIdInput, setFormIdInput] = useState(''); // Input for comma-separated form IDs
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [generatedCaseId, setGeneratedCaseId] = useState('');
  const [responseDetails, setResponseDetails] = useState(null);
  const [requestPayload, setRequestPayload] = useState(initialVisitData);
  const [domainStatus, setDomainStatus] = useState(null);
  const [formAssignmentResults, setFormAssignmentResults] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');

  // State for providers and health systems data
  const [providers, setProviders] = useState([]);
  const [healthSystems, setHealthSystems] = useState([]);
  const [specialties, setSpecialties] = useState([]); 
  const [dataLoadingError, setDataLoadingError] = useState('');
  const [testUuidIssues, setTestUuidIssues] = useState(false); // Toggle for testing UUID validation errors


  // Fetch health systems and specialties on component mount
  useEffect(() => {
    const fetchData = async () => {
      // Fetch Health Systems
      try {
        const healthSystemsResponse = await adminApi.getHealthSystems();
        if (healthSystemsResponse.data && healthSystemsResponse.data.success) {
          setHealthSystems(healthSystemsResponse.data.health_systems);
        } else {
          setDataLoadingError('Failed to fetch health systems.');
        }
      } catch (err) {
        setDataLoadingError('Error fetching health systems.');
      }

      // Fetch Specialties
      try {
        const specialtiesResponse = await adminApi.getSpecialties();
        if (specialtiesResponse.data && specialtiesResponse.data.success) {
          setSpecialties(specialtiesResponse.data.specialties); // Assuming the response has a 'specialties' key
        } else {
          setDataLoadingError(prev => prev + ' Failed to fetch specialties.');
        }
      } catch (err) {
        setDataLoadingError(prev => prev + ' Error fetching specialties.');
      }
    };

    fetchData();
  }, []); // Empty dependency array means this effect runs once on mount

  // Fetch providers when a health system is selected
  useEffect(() => {
    const healthSystemId = visitData.healthcare_info.health_system_id;

    // Only fetch providers if a health system is selected from the list (not manually entered)
    // We check if the healthSystemId exists in the healthSystems list to distinguish
    const isSelectedHealthSystem = healthSystems.some(hs => hs.health_system_id === healthSystemId);

    if (healthSystemId && isSelectedHealthSystem) {
      const fetchProviders = async () => {
        try {
          // Pass the selected health system ID directly to the getProviders API call
          const providersResponse = await adminApi.getProviders({ health_system_id: healthSystemId });
          
          if (providersResponse.data && providersResponse.data.success) {
            setProviders(providersResponse.data.providers || []); 
            setDataLoadingError(''); // Clear any previous data loading error
          } else {
            setDataLoadingError('Failed to fetch providers for the selected health system: ' + (providersResponse.data?.message || 'Unknown error'));
            setProviders([]); // Clear providers on error
          }
        } catch (err) {
          setDataLoadingError('Error fetching providers for the selected health system.');
          setProviders([]); // Clear providers on error
        }
      };

      fetchProviders();
    } else {
      // Clear providers if no health system is selected or if it's a manual entry
      setProviders([]);
    }

  }, [visitData.healthcare_info.health_system_id, healthSystems]); // Rerun when selected health system ID or healthSystems list changes


  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    // Handle nested state updates using dot notation in the name attribute (e.g., "patient_info.first_name" or "initial_forms_data.0.form_id")
    const nameParts = name.split('.');

    if (name === 'healthcare_info') {
      // Handle health system selection from dropdown
      const selectedHealthSystem = healthSystems.find(hs => hs.health_system_id === value);
      setVisitData(prev => {
        const newData = {
          ...prev,
          healthcare_info: selectedHealthSystem || initialVisitData.healthcare_info, // Use selected health system or reset if value is empty
          provider_info: initialVisitData.provider_info, // Clear provider info when health system changes
        };
        setRequestPayload(newData);
        return newData;
      });
      setProviders([]); // Clear providers when health system changes
    } else if (name === 'provider_info') {
      // Handle provider selection from dropdown
      // Find the provider by its unique 'id' (UUID)
      const selectedProvider = providers.find(p => p.id === value);
      setVisitData(prev => {
        const newData = {
          ...prev,
          provider_info: selectedProvider || initialVisitData.provider_info // Use selected provider or reset if value is empty
        };
        setRequestPayload(newData);
        return newData;
      });
    } else if (nameParts.length === 3) {
      // Handle updates to initial_forms_data array (e.g., "initial_forms_data.0.form_id")
      const arrayKey = nameParts[0]; // "initial_forms_data"
      const index = parseInt(nameParts[1], 10); // Array index
      const fieldName = nameParts[2]; // "form_id", "version", "saved_data", "last_updated_at"

      setVisitData(prev => {
        const newFormsData = [...prev[arrayKey]]; // Create a copy of the array
        const formToUpdate = { ...newFormsData[index] }; // Create a copy of the form object

        let updatedValue = val;
        if (fieldName === 'saved_data') {
          try {
            // Attempt to parse saved_data as JSON
            updatedValue = val ? JSON.parse(val) : null;
            setError(''); // Clear any previous JSON parsing error
          } catch (jsonError) {
            setError(`Invalid JSON for Saved Data in Form ${index + 1}. Please correct it.`);
            // Do not update the state with invalid JSON
            return prev;
          }
        } else if (fieldName === 'last_updated_at') {
           // For datetime-local input, the value is a string in "YYYY-MM-DDTHH:mm" format.
           // We need to convert it to ISO string format if it's not empty.
           updatedValue = val ? new Date(val).toISOString() : null;
        } else if (fieldName === 'form_id' || fieldName === 'version') {
          // Convert form_id and version to integers
          updatedValue = parseInt(val, 10);
          if (isNaN(updatedValue)) {
             // Handle case where parsing fails (e.g., user enters non-numeric text)
             updatedValue = val; // Keep the original string value or set to a default/error state
             // Optionally set an error state or prevent state update
             // setError(`Invalid number for ${fieldName} in Form ${index + 1}.`);
             // return prev;
          }
        }


        formToUpdate[fieldName] = updatedValue; // Update the specific field
        newFormsData[index] = formToUpdate; // Update the array with the modified form object

        const newData = {
          ...prev,
          [arrayKey]: newFormsData
        };

        // Update the request payload state to reflect the current form data
        setRequestPayload(newData);

        return newData;
      });
    } else if (nameParts.length > 1) {
      // Handle nested state updates using dot notation in the name attribute (e.g., "patient_info.first_name" or "healthcare_info.name")
      const topLevelKey = nameParts[0];
      const nestedKey = nameParts[1];

      setVisitData(prev => {
        const newData = {
          ...prev,
          [topLevelKey]: {
            ...prev[topLevelKey],
            [nestedKey]: val
          }
        };

        // If changing a manual healthcare input
        if (topLevelKey === 'healthcare_info') {
          if (nestedKey === 'health_system_id') {
            // If manually entering health_system_id, check if it matches any existing health system
            const matchingHealthSystem = healthSystems.find(hs => hs.health_system_id === val);
            if (matchingHealthSystem) {
              // If match found, update with the full health system data
              newData.healthcare_info = matchingHealthSystem;
            }
            
            // Clear providers when health system ID changes
            setProviders([]);
            newData.provider_info = initialVisitData.provider_info;
          }
          // For all other healthcare fields, just update the specific field without clearing others
        }
        // If changing a manual provider input
        if (topLevelKey === 'provider_info') {
          if (nestedKey === 'provider_id') {
            // If manually entering provider_id, check if it matches any existing provider
            const matchingProvider = providers.find(p => p.provider_id === val);
            if (matchingProvider) {
              // If match found, update with the full provider data
              newData.provider_info = matchingProvider;
            }
            // If no match found, just update the provider_id field without clearing other fields
          } else if (nestedKey === 'id') {
            // When provider id (UUID) is set, also set provider_uuid to ensure backend validation passes
            newData.provider_info.provider_uuid = val;
            const matchingProvider = providers.find(p => p.id === val);
            if (matchingProvider) {
              // If match found, update with the full provider data
              newData.provider_info = matchingProvider;
            }
            // If no match found, just update the id field without clearing other fields
          } else if (nestedKey === 'specialties') {
            // Handle multi-select for specialties
            newData.provider_info.specialties = Array.from(e.target.selectedOptions, option => option.value);
          }
        }



        // Update the request payload state to reflect the current form data
        setRequestPayload(newData);

        return newData;
      });
    } else {
      // Handle top-level state updates
      setVisitData(prev => {
        const newData = { ...prev, [name]: val };

        // Update the request payload state to reflect the current form data
        setRequestPayload(newData);

        return newData;
      });
    }
  };

  // Function to add a new form entry
  const handleAddForm = () => {
    setVisitData(prev => {
      const newFormsData = [
        ...prev.initial_forms_data,
        {
          form_id: '', // Default new form ID (empty string for input)
          version: 1, // Default version
          saved_data: '', // Default empty saved data (empty string for textarea)
          last_updated_at: '', // Default empty timestamp (empty string for datetime-local input)
        }
      ];
      const newData = { ...prev, initial_forms_data: newFormsData };
      setRequestPayload(newData); // Update request payload
      return newData;
    });
  };

  // Function to remove a form entry by index
  const handleRemoveForm = (indexToRemove) => {
    setVisitData(prev => {
      const newFormsData = prev.initial_forms_data.filter((_, index) => index !== indexToRemove);
      const newData = { ...prev, initial_forms_data: newFormsData };
      setRequestPayload(newData); // Update request payload
      return newData;
    });
  };


  // Handle form ID input change and update visitData.initial_forms_data
  // This function is no longer needed with dynamic form adding/removing,
  // but keeping it for now in case it's used elsewhere or for reference.
  const handleFormIdInputChange = (e) => {
    const value = e.target.value;
    setFormIdInput(value);
    try {
      const formIds = value.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));

      // Create form data entries for each ID with default values
      const formDataEntries = formIds.map(id => ({
        form_id: id,
        version: 1, // Integer
        saved_data: null,
        last_updated_at: null
      }));

      setVisitData(prev => {
        const newData = { ...prev, initial_forms_data: formDataEntries };

        // Update the request payload state to reflect the current form data
        setRequestPayload(newData);

        return newData;
      });
    } catch (error) {
      console.error('Error parsing form IDs:', error);
      // Optionally set an error state for invalid input
    }
  };

  // Helper function to generate a random date of birth (YYYY-MM-DD)
  const generateRandomDob = () => {
    const startYear = 1940;
    const endYear = 2005;
    const year = startYear + Math.floor(Math.random() * (endYear - startYear + 1));
    const month = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
    const day = String(Math.floor(Math.random() * 28) + 1).padStart(2, '0'); // Simple approach, avoids month/day complexities
    return `${year}-${month}-${day}`;
  };

  // Helper function to generate a random patient name
  const generateRandomPatientName = () => {
    const firstNames = ['Alice', 'Bob', 'Charlie', 'Diana', 'Ethan', 'Fiona', 'George', 'Hannah'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis'];
    const randomFirstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const randomLastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    return { first: randomFirstName, last: randomLastName };
  };

  // Helper function to generate a random seafaring captain name for providers
  const generateRandomCaptainName = () => {
    const captains = [
      { first: 'Jack', last: 'Sparrow' },
      { first: 'Horatio', last: 'Hornblower' },
      { first: 'James', last: 'Kirk' }, // Star Trek, but still a captain!
      { first: 'Jean-Luc', last: 'Picard' }, // Another Star Trek captain
      { first: 'Ahab', last: 'Spot' }, // Captain Ahab (Moby Dick) - no last name
      { first: 'Nemo', last: 'Orangephish' }, // Captain Nemo (Twenty Thousand Leagues Under the Seas) - no last name
      { first: 'William', last: 'Bligh' }, // Captain Bligh (Mutiny on the Bounty)
      { first: 'Christopher', last: 'Columbus' },
      { first: 'James', last: 'Cook' },
      { first: 'Ferdinand', last: 'Magellan' },
    ];
    const randomCaptain = captains[Math.floor(Math.random() * captains.length)];
    return randomCaptain;
  };


  // Helper function to generate a random healthcare name
  const generateRandomHealthcareName = () => {
    const prefixes = ['Community', 'Regional', 'St. Luke\'s', 'General', 'Memorial', 'City'];
    const suffixes = ['Hospital', 'Medical Center', 'Clinic', 'Health System', 'Care'];
    const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomSuffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    return `${randomPrefix} ${randomSuffix}`;
  };

  // Helper function to generate random appointment data
  const generateRandomAppointmentData = () => {
    const appointmentTypes = [
      'Annual Physical', 'Follow-up', 'Consultation', 'Urgent Care', 
      'Wellness Check', 'Specialist Consultation', 'Pre-operative Assessment', 
      'Post-operative Follow-up', 'Medication Review', 'Lab Results Review'
    ];
    
    const serviceCategories = ['Outpatient', 'Inpatient', 'Emergency', 'Telehealth', 'Home Visit'];
    const serviceTypes = ['Consultation', 'Procedure', 'Diagnostic', 'Treatment', 'Preventive Care'];
    const specialties = ['Family Medicine', 'Cardiology', 'Neurology', 'Orthopedics', 'Pediatrics', 'Dermatology'];
    const ehrSystems = ['Epic', 'Cerner', 'Allscripts', 'eClinicalWorks', 'NextGen'];
    const priorities = ['routine', 'urgent', 'emergent', 'asap'];
    const confirmationStatuses = ['unconfirmed', 'confirmed', 'cancelled', 'rescheduled'];
    
    // Generate appointment time (1-7 days from now)
    const appointmentDate = new Date();
    appointmentDate.setDate(appointmentDate.getDate() + Math.floor(Math.random() * 7) + 1);
    appointmentDate.setHours(8 + Math.floor(Math.random() * 10), Math.floor(Math.random() * 4) * 15, 0, 0); // 8AM-6PM, 15min intervals
    
    const duration = [15, 30, 45, 60, 90][Math.floor(Math.random() * 5)]; // Common appointment durations
    const endDate = new Date(appointmentDate.getTime() + duration * 60000);
    
    const selectedAppointmentType = appointmentTypes[Math.floor(Math.random() * appointmentTypes.length)];
    const selectedSpecialty = specialties[Math.floor(Math.random() * specialties.length)];
    
    return {
      ehr_appointment_id: `APPT-${Math.floor(Math.random() * 1000000)}`,
      ehr_encounter_id: `ENC-${Math.floor(Math.random() * 1000000)}`,
      ehr_system: ehrSystems[Math.floor(Math.random() * ehrSystems.length)],
      appointment_type: selectedAppointmentType,
      appointment_type_ehr_code: `APT_${selectedAppointmentType.replace(/\s+/g, '_').toUpperCase()}`,
      start_datetime: appointmentDate.toISOString(),
      end_datetime: endDate.toISOString(),
      minutes_duration: duration,
      timezone: 'America/Chicago',
      service_category: serviceCategories[Math.floor(Math.random() * serviceCategories.length)],
      service_type: serviceTypes[Math.floor(Math.random() * serviceTypes.length)],
      specialty_text: selectedSpecialty,
      reason: `Patient scheduled for ${selectedAppointmentType.toLowerCase()}`,
      priority: priorities[Math.floor(Math.random() * priorities.length)],
      department_name: selectedSpecialty,
      department_ehr_id: `DEPT_${selectedSpecialty.replace(/\s+/g, '_').toUpperCase()}`,
      clinic_name: 'Main Clinic',
      clinic_ehr_id: 'CLINIC_MAIN',
      description: `${selectedAppointmentType} appointment with ${selectedSpecialty} department`,
      comments: Math.random() > 0.5 ? 'Patient requested morning appointment' : '',
      notes: Math.random() > 0.5 ? 'Review previous test results' : '',
      patient_instructions: Math.random() > 0.5 ? 'Please arrive 15 minutes early for check-in' : '',
      confirmation_status: confirmationStatuses[Math.floor(Math.random() * confirmationStatuses.length)],
      copay_amount: [0, 10, 15, 20, 25, 30, 35, 40, 50][Math.floor(Math.random() * 9)],
      authorization_required: Math.random() > 0.7,
      follow_up_required: Math.random() > 0.6,
      // Fields that go into other_info
      room: `${Math.floor(Math.random() * 20) + 101}`,
      floor: `${Math.floor(Math.random() * 5) + 1}`,
      building: ['Main Building', 'West Wing', 'East Wing', 'Medical Tower'][Math.floor(Math.random() * 4)],
      provider_name: '', // Will be filled from provider info
      provider_npi: `${Math.floor(1000000000 + Math.random() * 9000000000)}`,
      provider_ehr_id: `PROV_EHR_${Math.floor(Math.random() * 10000)}`,
      custom_fields: {
        special_needs: Math.random() > 0.8 ? 'wheelchair accessible' : null,
        interpreter_required: Math.random() > 0.9 ? 'Spanish' : null,
        parking_validation: Math.random() > 0.7
      }
    };
  };

  // Helper function to generate UUID error scenarios for testing
  const generateUuidErrorScenarios = () => {
    const scenarios = [
      {
        name: 'empty_uuids',
        patient_uuid: '',
        provider_uuid: '',
        patient_novu_id: '',
        provider_novu_id: ''
      },
      {
        name: 'invalid_format_short',
        patient_uuid: '123-456-789',
        provider_uuid: 'abc-def-ghi',
        patient_novu_id: '123-456-789',
        provider_novu_id: 'abc-def-ghi'
      },
      {
        name: 'invalid_format_text',
        patient_uuid: 'not-a-uuid-at-all',
        provider_uuid: 'definitely-not-uuid',
        patient_novu_id: 'not-a-uuid-at-all',
        provider_novu_id: 'definitely-not-uuid'
      },
      {
        name: 'malformed_almost_valid',
        patient_uuid: '123e4567-e89b-12d3-a456-42661417400', // missing one character
        provider_uuid: '987fcdeb-51a2-43d1-9f12-345678901234x', // extra character
        patient_novu_id: '123e4567-e89b-12d3-a456-42661417400',
        provider_novu_id: '987fcdeb-51a2-43d1-9f12-345678901234x'
      },
      {
        name: 'mixed_valid_invalid',
        patient_uuid: crypto.randomUUID(), // valid
        provider_uuid: 'invalid-provider-uuid', // invalid
        patient_novu_id: crypto.randomUUID(),
        provider_novu_id: 'invalid-provider-uuid'
      },
      {
        name: 'wrong_version_uuid',
        patient_uuid: '123e4567-e89b-22d3-a456-426614174000', // UUID v2 instead of v4
        provider_uuid: '987fcdeb-51a2-33d1-9f12-345678901234', // UUID v3 instead of v4
        patient_novu_id: '123e4567-e89b-22d3-a456-426614174000',
        provider_novu_id: '987fcdeb-51a2-33d1-9f12-345678901234'
      }
    ];
    
    return scenarios[Math.floor(Math.random() * scenarios.length)];
  };

  // Helper function to generate valid UUIDs
  const generateValidUuids = () => {
    const patientUuid = crypto.randomUUID();
    const providerUuid = crypto.randomUUID();
    
    return {
      name: 'valid_uuids',
      patient_uuid: patientUuid,
      provider_uuid: providerUuid,
      patient_novu_id: patientUuid, // Same as patient UUID in our architecture
      provider_novu_id: providerUuid // Same as provider UUID in our architecture
    };
  };

  // Function to populate form with dummy data
  const populateWithDummyData = () => {
    // Generate dummy form data with integer versions, saved data, and timestamps
    const dummyFormData = [
      {
        form_id: 1,
        version: 5, // Integer version (not string)
        saved_data: { responses: { q1: "Yes", q2: "No" } },
        last_updated_at: new Date(Date.now() - 86400000).toISOString() // 1 day ago
      },
      {
        form_id: 2,
        version: 1, // Integer version (not string)
        saved_data: { responses: { q1: "Option A", q2: "Text response here" } },
        last_updated_at: new Date(Date.now() - 172800000).toISOString() // 2 days ago
      },
      {
        form_id: 5,
        version: 1, // Integer version (not string)
        saved_data: null,
        last_updated_at: null
      },
      // Add a form with a non-existent ID to test error handling
      {
        form_id: 999,
        version: 1,
        saved_data: null,
        last_updated_at: null
      }
    ];

    // Generate random insurance providers
    const insuranceProviders = ['Blue Cross Blue Shield', 'Aetna', 'UnitedHealthcare', 'Cigna', 'Humana', 'Kaiser Permanente'];
    const randomInsuranceProvider = insuranceProviders[Math.floor(Math.random() * insuranceProviders.length)];

    const patientName = generateRandomPatientName();
    const providerName = generateRandomCaptainName(); // Use captain names for providers
    const healthcareName = generateRandomHealthcareName();
    const appointmentData = generateRandomAppointmentData();

    // Generate UUIDs based on toggle state
    const uuidData = testUuidIssues ? generateUuidErrorScenarios() : generateValidUuids();

    // Create a single dummy data object to use for both states
    const dummyData = {
      CoreServer_CaseID: `dc_${Date.now()}`,
      patient_info: {
        patient_id: `PATIENT-${Math.floor(Math.random() * 100000)}`,
        patient_uuid: uuidData.patient_uuid,
        novu_subscriber_id: uuidData.patient_novu_id,
        first_name: patientName.first,
        last_name: patientName.last,
        dob: generateRandomDob(),
        email: `patient${Math.floor(Math.random() * 1000)}@800ambi.com`,
        phone: `+1${Math.floor(1000000000 + Math.random() * 9000000000)}`,
        locale: 'en-US',
        timezone: 'America/Chicago',
      },
      provider_info: {
        id: uuidData.provider_uuid,
        provider_id: `PROVIDER-${Math.floor(Math.random() * 1000)}`,
        provider_uuid: uuidData.provider_uuid,
        novu_subscriber_id: uuidData.provider_novu_id,
        first_name: providerName.first,
        middle_name: null,
        last_name: providerName.last,
        specialties: [['Cardiology'], ['Pediatrics'], ['Neurology'], ['Oncology'], ['Primary Care']][Math.floor(Math.random() * 5)],
        provider_type: 'Doctor',
        phone: `+1${Math.floor(1000000000 + Math.random() * 9000000000)}`,
        phone_2: null,
        email: `provider${Math.floor(Math.random() * 1000)}@800ambi.com`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_active: true,
        health_system: null,
        timezone: 'America/Chicago',
      },
      healthcare_info: {
        health_system_id: `HS-${Math.floor(Math.random() * 100)}`,
        name: healthcareName,
      },
      initial_forms_data: dummyFormData,
      initial_insurance_data: {
        insurance_provider: randomInsuranceProvider,
        member_id: `MEM${Math.floor(Math.random() * 10000000)}`,
        group_number: `GRP${Math.floor(Math.random() * 10000)}`,
        front_image_path: 'https://example.com/insurance-card-front.jpg',
        back_image_path: 'https://example.com/insurance-card-back.jpg',
        verification_status: ['pending', 'verified'][Math.floor(Math.random() * 2)],
      },
      appointment_info: {
        ...appointmentData,
        provider_name: `${providerName.first} ${providerName.last}` // Fill in provider name
      },
      include_zoom_session: Math.random() > 0.5 // Randomly set to true or false
    };
    
    // Update both states with the same data
    setVisitData(dummyData);
    setRequestPayload(dummyData);
    
    // Update other states
    setError('');
    setSuccessMessage('');
    setGeneratedUrl('');
    setGeneratedCaseId('');
    setResponseDetails(null);
  };


  const Modal = ({ show, title, content, onClose, onCopy, copySuccessMap }) => {
    if (!show) {
      return null;
    }

    return (
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">{title}</h3>
              <div className="d-flex ms-auto">
                <button type="button" className="btn btn-sm btn-info me-2" onClick={() => onCopy(content, 'modalContent')}>
                  {copySuccessMap['modalContent'] || 'Copy to Clipboard'}
                </button>
                <button type="button" className="btn btn-close" style={{margin:0}} aria-label="Close" onClick={onClose}></button>
              </div>
            </div>
            <div className="modal-body">
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'black' }}>{content}</pre>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Initiate visit using backend endpoint
  const initiateVisit = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    setGeneratedUrl('');
    setGeneratedCaseId('');
    setResponseDetails(null);

    try {
      // Set the request payload first to ensure it's available for display
      // Set the request payload first to ensure it's available for display
      setRequestPayload({...visitData});
      
      // Validate required fields before submitting
      if (!visitData.patient_info.patient_id || !visitData.patient_info.first_name || !visitData.patient_info.last_name || !visitData.patient_info.dob) {
        setError('Please fill out all required patient information.');
        setIsLoading(false);
        return;
      }
      
      // Validate DOB format (YYYY-MM-DD)
      const dobRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dobRegex.test(visitData.patient_info.dob)) {
        setError('Date of Birth must be in YYYY-MM-DD format.');
        setIsLoading(false);
        return;
      }
      
      if (visitData.initial_forms_data.length === 0) {
        setError('Please provide at least one Form ID.');
        setIsLoading(false);
        return;
      }
      
      // Validate insurance data if provided
      if (visitData.initial_insurance_data.insurance_provider && !visitData.initial_insurance_data.member_id) {
        setError('If providing insurance information, Member ID is required.');
        setIsLoading(false);
        return;
      }

      // Get the admin token from localStorage
      // Make the API request
      const response = await adminApi.initiateVisit(visitData);

      if (response.data && response.data.success) {
        setSuccessMessage('Patient check-in initiated successfully!');
        setGeneratedUrl(response.data.short_url);
        setGeneratedCaseId(response.data.case_id);
        setResponseDetails(response.data);
        
        // Store domain status and form assignment results for detailed display
        if (response.data.domain_status) {
          setDomainStatus(response.data.domain_status);
          
          // Extract form assignment results from domain_status.forms.assignments
          if (response.data.domain_status.forms && response.data.domain_status.forms.assignments) {
            setFormAssignmentResults(response.data.domain_status.forms.assignments);
          }
        }
      } else {
        setError(response.data?.message || 'Failed to initiate visit. Invalid server response.');
        
        // Even on error, store domain status if available for debugging
        if (response.data?.domain_status) {
          setDomainStatus(response.data.domain_status);
          
          // Extract form assignment results from domain_status even on error
          if (response.data.domain_status.forms && response.data.domain_status.forms.assignments) {
            setFormAssignmentResults(response.data.domain_status.forms.assignments);
          }
        }
      }
    } catch (error) {
      // Extract detailed error information if available
      let errorMessage = error.response?.data?.error || error.message;
      
      // Check if there's domain status information in the error response
      if (error.response?.data?.domain_status) {
        setDomainStatus(error.response.data.domain_status);
        
        // Extract form assignment results from error response domain_status
        if (error.response.data.domain_status.forms && error.response.data.domain_status.forms.assignments) {
          setFormAssignmentResults(error.response.data.domain_status.forms.assignments);
        }
        
        // Find the first domain with an error status to display a more specific message
        const errorDomain = Object.entries(error.response.data.domain_status)
          .find(([_, domainData]) => domainData.status === 'error');
          
        if (errorDomain) {
          errorMessage = `Error in ${errorDomain[0]}: ${errorDomain[1].message}`;
        }
      }
      
      setError(`Error initiating visit: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Initiate Patient Visit</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
                <li className="breadcrumb-item active">Initiate Visit</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      <div className="">
        {/* <div className="card-header">
          <h4 className="card-title mb-0">Visit Details</h4>
        </div> */}
        <div className="card-body">
          <div className="card p-3 mb-0">
          <div className="col-md-3">
              <label htmlFor="CoreServer_CaseID" className="form-label">Core Server Case ID <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="CoreServer_CaseID"
                name="CoreServer_CaseID"
                value={visitData.CoreServer_CaseID}
                onChange={handleInputChange}
                placeholder="Unique Ankr Case ID from Core Server"
                required
              />
              </div>
          </div>
          {/* Patient Information */}
          <div className="card p-3">
          <h5 className="card-title text-primary mb-3">Patient Information</h5>
          <div className="row mb-3">
            <div className="col-md-3">
              <label htmlFor="patient_info.patient_uuid" className="form-label">Patient UUID <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="patient_info.patient_uuid"
                name="patient_info.patient_uuid"
                value={visitData.patient_info.patient_uuid}
                onChange={handleInputChange}
                placeholder="UUID from Core Server"
                required
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="patient_info.patient_id" className="form-label">Patient ID (Legacy)</label>
              <input
                type="text"
                className="form-control"
                id="patient_info.patient_id"
                name="patient_info.patient_id"
                value={visitData.patient_info.patient_id}
                onChange={handleInputChange}
                placeholder="Enter patient ID (optional)"
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="patient_info.novu_subscriber_id" className="form-label">Novu Subscriber ID</label>
              <input
                type="text"
                className="form-control"
                id="patient_info.novu_subscriber_id"
                name="patient_info.novu_subscriber_id"
                value={visitData.patient_info.novu_subscriber_id}
                onChange={handleInputChange}
                placeholder="If empty, will use patient UUID"
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="patient_info.first_name" className="form-label">First Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="patient_info.first_name"
                name="patient_info.first_name"
                value={visitData.patient_info.first_name}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-3">
              <label htmlFor="patient_info.last_name" className="form-label">Last Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="patient_info.last_name"
                name="patient_info.last_name"
                value={visitData.patient_info.last_name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="patient_info.dob" className="form-label">Date of Birth (YYYY-MM-DD) <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="patient_info.dob"
                name="patient_info.dob"
                value={visitData.patient_info.dob}
                onChange={handleInputChange}
                placeholder="YYYY-MM-DD"
                required
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-3">
              <label htmlFor="patient_info.email" className="form-label">Email <span className="text-danger">*</span></label>
              <input
                type="email"
                className="form-control"
                id="patient_info.email"
                name="patient_info.email"
                value={visitData.patient_info.email}
                onChange={handleInputChange}
                placeholder="patient@example.com"
                required
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="patient_info.phone" className="form-label">Phone <span className="text-danger">*</span></label>
              <input
                type="tel"
                className="form-control"
                id="patient_info.phone"
                name="patient_info.phone"
                value={visitData.patient_info.phone}
                onChange={handleInputChange}
                placeholder="e.g., +15551234567"
                required
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="patient_info.locale" className="form-label">Locale <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="patient_info.locale"
                name="patient_info.locale"
                value={visitData.patient_info.locale}
                onChange={handleInputChange}
                placeholder="e.g., en-US"
                required
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="patient_info.timezone" className="form-label">Timezone <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                id="patient_info.timezone"
                name="patient_info.timezone"
                value={visitData.patient_info.timezone}
                onChange={handleInputChange}
                placeholder="e.g., America/Chicago"
                required
              />
            </div>
          </div>
          </div>

          {/* Healthcare Info */}
          <div className="card p-3">
          <h5 className="card-title text-primary">Healthcare Information</h5>
          <p className="card-subtitle text-secondary mb-3">Select from existing db entires in MiraSol or create new</p>
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="healthcare_info" className="form-label">Select Health System</label>
              <select
                className="form-select mb-2"
                id="healthcare_info"
                name="healthcare_info"
                value={visitData.healthcare_info.health_system_id || ''} // Use health_system_id for select value
                onChange={handleInputChange}
              >
                <option value="">---Awaiting your selection---</option>
                {healthSystems.map(system => (
                  <option key={system.health_system_id} value={system.health_system_id}>
                    {system.name}
                  </option>
                ))}
              </select>
              </div>
              {/* Manual Health System Input (always shown) */}
              <div className="col-md-6">
                <div className="">
                  <label htmlFor="healthcare_info.health_system_id" className="form-label">Health System ID <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    id="healthcare_info.health_system_id"
                    name="healthcare_info.health_system_id"
                    value={visitData.healthcare_info.health_system_id}
                    onChange={handleInputChange}
                    placeholder="Enter health system ID or select from list"
                    required
                  />
                </div>
                <div className="">
                  <label htmlFor="healthcare_info.name" className="form-label">Health System Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    id="healthcare_info.name"
                    name="healthcare_info.name"
                    value={visitData.healthcare_info.name}
                    onChange={handleInputChange}
                    placeholder="Enter health system name or select from list"
                    required
                  />
                </div>
              </div>
              {dataLoadingError && <div className="text-danger mt-2">{dataLoadingError}</div>}
            </div>
          </div>

          {/* Provider Info */}
          <div className="card p-3">
          <h5 className="card-title text-primary mb-3">Provider Information (Select from existing db entires in MiraSol or create new)</h5>
          <div className="row mb-3">
            <div className="col-md-3">
              <label htmlFor="provider_info" className="form-label">Select Provider</label>
              <select
                className="form-select mb-2"
                id="provider_info"
                name="provider_info"
                value={visitData.provider_info.id || ''} // Use provider.id for select value
                onChange={handleInputChange}
                disabled={!visitData.healthcare_info.health_system_id} // Disable until health system is selected
              >
                <option value="">---Awaiting your selection---</option>
                {providers.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.provider_id} - {provider.first_name} {provider.last_name} ({provider.specialties && provider.specialties.length > 0 ? provider.specialties.map(s => s.name).join(', ') : 'N/A'})
                  </option>
                ))}
              </select>
              </div>
              {/* Provider Details - Always shown */}
              <div className="col-md-9">
                <div className="row g-5">
                <div className="col-md-4">
                  <label htmlFor="provider_info.id" className="form-label">Provider UUID <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    id="provider_info.id"
                    name="provider_info.id"
                    value={visitData.provider_info.id}
                    onChange={handleInputChange}
                    placeholder="UUID from Core Server"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.provider_id" className="form-label">Provider ID (Legacy)</label>
                  <input
                    type="text"
                    className="form-control"
                    id="provider_info.provider_id"
                    name="provider_info.provider_id"
                    value={visitData.provider_info.provider_id}
                    onChange={handleInputChange}
                    placeholder="Enter provider ID (optional)"
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.novu_subscriber_id" className="form-label">Novu Subscriber ID</label>
                  <input
                    type="text"
                    className="form-control"
                    id="provider_info.novu_subscriber_id"
                    name="provider_info.novu_subscriber_id"
                    value={visitData.provider_info.novu_subscriber_id}
                    onChange={handleInputChange}
                    placeholder="If empty, will use provider UUID"
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.first_name" className="form-label">Provider First Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    id="provider_info.first_name"
                    name="provider_info.first_name"
                    value={visitData.provider_info.first_name}
                    onChange={handleInputChange}
                    placeholder="Enter first name or select from above"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.middle_name" className="form-label">Provider Middle Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="provider_info.middle_name"
                    name="provider_info.middle_name"
                    value={visitData.provider_info.middle_name || ''}
                    onChange={handleInputChange}
                    placeholder="Enter middle name (optional)"
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.last_name" className="form-label">Provider Last Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    id="provider_info.last_name"
                    name="provider_info.last_name"
                    value={visitData.provider_info.last_name}
                    onChange={handleInputChange}
                    placeholder="Enter last name or select from above"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.provider_type" className="form-label">Provider Type <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    id="provider_info.provider_type"
                    name="provider_info.provider_type"
                    value={visitData.provider_info.provider_type}
                    onChange={handleInputChange}
                    placeholder="Doctor, APP, Nurse, Social Worker etc."
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.phone" className="form-label">Provider Phone <span className="text-danger">*</span></label>
                  <input
                    type="tel"
                    className="form-control"
                    id="provider_info.phone"
                    name="provider_info.phone"
                    value={visitData.provider_info.phone}
                    onChange={handleInputChange}
                    placeholder="e.g., +15551234567"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.phone_2" className="form-label">Provider Phone 2</label>
                  <input
                    type="tel"
                    className="form-control"
                    id="provider_info.phone_2"
                    name="provider_info.phone_2"
                    value={visitData.provider_info.phone_2 || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., +15551234567 (optional)"
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.email" className="form-label">Provider Email <span className="text-danger">*</span></label>
                  <input
                    type="email"
                    className="form-control"
                    id="provider_info.email"
                    name="provider_info.email"
                    value={visitData.provider_info.email}
                    onChange={handleInputChange}
                    placeholder="provider@example.com"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.specialties" className="form-label">Specialties</label>
                  <select
                    className="form-select"
                    id="provider_info.specialties"
                    name="provider_info.specialties"
                    multiple // Enable multi-select
                    value={visitData.provider_info.specialties || []} // Value is an array of selected options
                    onChange={handleInputChange}
                    style={{ minHeight: '100px' }} // Adjust height for multi-select
                  >
                    {specialties.map(s => (
                      <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                  <div className="mt-2">
                    <label htmlFor="provider_info.new_specialty" className="form-label">Add New Specialty</label>
                    <input
                      type="text"
                      className="form-control"
                      id="provider_info.new_specialty"
                      name="provider_info.new_specialty"
                      placeholder="Type new specialty and press Enter"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim() !== '') {
                          e.preventDefault(); // Prevent form submission
                          const newSpecialty = e.target.value.trim();
                          setVisitData(prev => ({
                            ...prev,
                            provider_info: {
                              ...prev.provider_info,
                              specialties: [...new Set([...prev.provider_info.specialties, newSpecialty])] // Add new specialty, prevent duplicates
                            }
                          }));
                          e.target.value = ''; // Clear input field
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.timezone" className="form-label">Provider Timezone <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    id="provider_info.timezone"
                    name="provider_info.timezone"
                    value={visitData.provider_info.timezone}
                    onChange={handleInputChange}
                    placeholder="e.g., America/Chicago"
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="provider_info.is_active" className="form-label">Is Active</label>
                  <select
                    className="form-select"
                    id="provider_info.is_active"
                    name="provider_info.is_active"
                    value={visitData.provider_info.is_active}
                    onChange={handleInputChange}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div></div>
              </div>
              {dataLoadingError && <div className="text-danger mt-2">{dataLoadingError}</div>}
            </div>
          </div>

          {/* Insurance Information */}
          <div className="card p-3">
          <h5 className="card-title text-primary mb-3">Insurance Information (Optional)</h5>
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="initial_insurance_data.insurance_provider" className="form-label">Insurance Provider</label>
              <input
                type="text"
                className="form-control"
                id="initial_insurance_data.insurance_provider"
                name="initial_insurance_data.insurance_provider"
                value={visitData.initial_insurance_data.insurance_provider}
                onChange={handleInputChange}
                placeholder="e.g., Blue Cross Blue Shield"
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="initial_insurance_data.member_id" className="form-label">Member ID / Policy Number</label>
              <input
                type="text"
                className="form-control"
                id="initial_insurance_data.member_id"
                name="initial_insurance_data.member_id"
                value={visitData.initial_insurance_data.member_id}
                onChange={handleInputChange}
                placeholder="Enter member ID"
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="initial_insurance_data.group_number" className="form-label">Group Number</label>
              <input
                type="text"
                className="form-control"
                id="initial_insurance_data.group_number"
                name="initial_insurance_data.group_number"
                value={visitData.initial_insurance_data.group_number}
                onChange={handleInputChange}
                placeholder="Enter group number"
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="initial_insurance_data.verification_status" className="form-label">Verification Status</label>
              <select
                className="form-select"
                id="initial_insurance_data.verification_status"
                name="initial_insurance_data.verification_status"
                value={visitData.initial_insurance_data.verification_status}
                onChange={handleInputChange}
              >
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
          </div>
          
          {/* Appointment Information */}
          <div className="card p-3">
          <h5 className="card-title text-primary mb-3">Appointment Information (Optional)</h5>
          <div className="row mb-3">
            <div className="col-md-4">
              <label htmlFor="appointment_info.ehr_appointment_id" className="form-label">EHR Appointment ID</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.ehr_appointment_id"
                name="appointment_info.ehr_appointment_id"
                value={visitData.appointment_info.ehr_appointment_id}
                onChange={handleInputChange}
                placeholder="External appointment ID"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="appointment_info.ehr_encounter_id" className="form-label">EHR Encounter ID</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.ehr_encounter_id"
                name="appointment_info.ehr_encounter_id"
                value={visitData.appointment_info.ehr_encounter_id}
                onChange={handleInputChange}
                placeholder="External encounter ID"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="appointment_info.ehr_system" className="form-label">EHR System</label>
              <select
                className="form-select"
                id="appointment_info.ehr_system"
                name="appointment_info.ehr_system"
                value={visitData.appointment_info.ehr_system}
                onChange={handleInputChange}
              >
                <option value="">Select EHR System</option>
                <option value="Epic">Epic</option>
                <option value="Cerner">Cerner</option>
                <option value="Allscripts">Allscripts</option>
                <option value="eClinicalWorks">eClinicalWorks</option>
                <option value="NextGen">NextGen</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-4">
              <label htmlFor="appointment_info.appointment_type" className="form-label">Appointment Type</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.appointment_type"
                name="appointment_info.appointment_type"
                value={visitData.appointment_info.appointment_type}
                onChange={handleInputChange}
                placeholder="e.g., Annual Physical, Follow-up"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="appointment_info.priority" className="form-label">Priority</label>
              <select
                className="form-select"
                id="appointment_info.priority"
                name="appointment_info.priority"
                value={visitData.appointment_info.priority}
                onChange={handleInputChange}
              >
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="emergent">Emergent</option>
                <option value="asap">ASAP</option>
              </select>
            </div>
            <div className="col-md-4">
              <label htmlFor="appointment_info.confirmation_status" className="form-label">Confirmation Status</label>
              <select
                className="form-select"
                id="appointment_info.confirmation_status"
                name="appointment_info.confirmation_status"
                value={visitData.appointment_info.confirmation_status}
                onChange={handleInputChange}
              >
                <option value="unconfirmed">Unconfirmed</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="appointment_info.start_datetime" className="form-label">Appointment Start Time</label>
              <input
                type="datetime-local"
                className="form-control"
                id="appointment_info.start_datetime"
                name="appointment_info.start_datetime"
                value={visitData.appointment_info.start_datetime ? visitData.appointment_info.start_datetime.substring(0, 16) : ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="appointment_info.end_datetime" className="form-label">Appointment End Time</label>
              <input
                type="datetime-local"
                className="form-control"
                id="appointment_info.end_datetime"
                name="appointment_info.end_datetime"
                value={visitData.appointment_info.end_datetime ? visitData.appointment_info.end_datetime.substring(0, 16) : ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-3">
              <label htmlFor="appointment_info.minutes_duration" className="form-label">Duration (minutes)</label>
              <input
                type="number"
                className="form-control"
                id="appointment_info.minutes_duration"
                name="appointment_info.minutes_duration"
                value={visitData.appointment_info.minutes_duration}
                onChange={handleInputChange}
                placeholder="30"
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="appointment_info.copay_amount" className="form-label">Copay Amount</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                id="appointment_info.copay_amount"
                name="appointment_info.copay_amount"
                value={visitData.appointment_info.copay_amount}
                onChange={handleInputChange}
                placeholder="25.00"
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="appointment_info.room" className="form-label">Room</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.room"
                name="appointment_info.room"
                value={visitData.appointment_info.room}
                onChange={handleInputChange}
                placeholder="101"
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="appointment_info.floor" className="form-label">Floor</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.floor"
                name="appointment_info.floor"
                value={visitData.appointment_info.floor}
                onChange={handleInputChange}
                placeholder="2"
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-4">
              <label htmlFor="appointment_info.department_name" className="form-label">Department</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.department_name"
                name="appointment_info.department_name"
                value={visitData.appointment_info.department_name}
                onChange={handleInputChange}
                placeholder="Cardiology"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="appointment_info.clinic_name" className="form-label">Clinic</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.clinic_name"
                name="appointment_info.clinic_name"
                value={visitData.appointment_info.clinic_name}
                onChange={handleInputChange}
                placeholder="Main Clinic"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="appointment_info.specialty_text" className="form-label">Specialty</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.specialty_text"
                name="appointment_info.specialty_text"
                value={visitData.appointment_info.specialty_text}
                onChange={handleInputChange}
                placeholder="Family Medicine"
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-12">
              <label htmlFor="appointment_info.reason" className="form-label">Reason for Visit</label>
              <input
                type="text"
                className="form-control"
                id="appointment_info.reason"
                name="appointment_info.reason"
                value={visitData.appointment_info.reason}
                onChange={handleInputChange}
                placeholder="Patient scheduled for annual physical"
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="appointment_info.patient_instructions" className="form-label">Patient Instructions</label>
              <textarea
                className="form-control"
                id="appointment_info.patient_instructions"
                name="appointment_info.patient_instructions"
                value={visitData.appointment_info.patient_instructions}
                onChange={handleInputChange}
                rows="2"
                placeholder="Please arrive 15 minutes early for check-in"
              ></textarea>
            </div>
            <div className="col-md-6">
              <label htmlFor="appointment_info.notes" className="form-label">Appointment Notes</label>
              <textarea
                className="form-control"
                id="appointment_info.notes"
                name="appointment_info.notes"
                value={visitData.appointment_info.notes}
                onChange={handleInputChange}
                rows="2"
                placeholder="Review previous test results"
              ></textarea>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="appointment_info.authorization_required"
                  name="appointment_info.authorization_required"
                  checked={visitData.appointment_info.authorization_required}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="appointment_info.authorization_required">
                  Authorization Required
                </label>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="appointment_info.follow_up_required"
                  name="appointment_info.follow_up_required"
                  checked={visitData.appointment_info.follow_up_required}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="appointment_info.follow_up_required">
                  Follow-up Required
                </label>
              </div>
            </div>
          </div>
          </div>
          
          {/* Form Data */}
          <div className="card p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title text-primary mb-0">Patient Forms <span className="text-danger">*</span></h5>
            <button className="btn btn-success btn-sm" onClick={handleAddForm}>
              <i className="fas fa-plus me-1"></i> Add Form
            </button>
          </div>
          {visitData.initial_forms_data.map((form, index) => (
            <div key={index} className="card border mb-3">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="card-title mb-0">Form {index + 1}</h6>
                  <button className="btn btn-danger btn-sm" onClick={() => handleRemoveForm(index)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
                <div className="row gy-3">
                  <div className="col-md-3">
                    <label htmlFor={`form_id-${index}`} className="form-label">Form ID <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className="form-control"
                      id={`form_id-${index}`}
                      name={`initial_forms_data.${index}.form_id`}
                      value={form.form_id}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor={`version-${index}`} className="form-label">Version <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className="form-control"
                      id={`version-${index}`}
                      name={`initial_forms_data.${index}.version`}
                      value={form.version}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor={`last_updated_at-${index}`} className="form-label">Last Updated At</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id={`last_updated_at-${index}`}
                      name={`initial_forms_data.${index}.last_updated_at`}
                      value={form.last_updated_at ? form.last_updated_at.substring(0, 16) : ''} // Format for datetime-local input
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor={`saved_data-${index}`} className="form-label">Saved Data (JSON)</label>
                    <textarea
                      className="form-control"
                      id={`saved_data-${index}`}
                      name={`initial_forms_data.${index}.saved_data`}
                      value={form.saved_data ? JSON.stringify(form.saved_data, null, 2) : ''}
                      onChange={handleInputChange}
                      rows="4"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          ))}</div>

           {/* Zoom Session Toggle */}
          <div className="card p-3">
           <h5 className="card-title text-primary mb-3">Zoom Session</h5>
           <div className="row mb-3">
             <div className="col-md-12">
               <div className="form-check form-switch">
                 <input
                   className="form-check-input"
                   type="checkbox"
                   id="include_zoom_session"
                   name="include_zoom_session"
                   checked={visitData.include_zoom_session}
                   onChange={handleInputChange}
                 />
                 <label className="form-check-label" htmlFor="include_zoom_session">
                   Create Zoom Session
                 </label>
                 <div className="form-text">
                   Enable this option to create a Zoom video session with this visit.
                   The backend will automatically generate appropriate Zoom meeting details.
                 </div>
               </div>
             </div>
           </div>
           </div>

          {/* UUID Testing Information */}
          {testUuidIssues && (
            <div className="alert alert-warning mt-3 mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>UUID Testing Mode Enabled:</strong> The Surprise me button will generate data with various UUID validation errors to test backend error handling. This includes empty UUIDs, invalid formats, and malformed UUIDs.
            </div>
          )}

          {/* Action buttons */}
          <div className="d-flex mt-4">
            {/* Initiate Visit button */}
            <button
              className="btn btn-primary waves-effect waves-light me-2"
              onClick={initiateVisit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Initiating...
                </>
              ) : (
                <>
                  <i className="fas fa-play-circle me-1"></i> Initiate Visit
                </>
              )}
            </button>
            {/* Surprise me button */}
            <button
              className="btn btn-secondary waves-effect waves-light me-2"
              onClick={populateWithDummyData}
              disabled={isLoading}
              title={testUuidIssues ? "Generate data with UUID validation errors for testing" : "Generate valid dummy data"}
            >
              <i className="fas fa-random me-1"></i>
              {testUuidIssues ? "Surprise me (with errors)" : "Surprise me"}
            </button>
            

            {/* Test UUID Issues Toggle */}
            <div className="btn form-check form-switch mx-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="testUuidIssuesToggle"
                checked={testUuidIssues}
                onChange={(e) => setTestUuidIssues(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="testUuidIssuesToggle">
                Test UUID Issues
              </label>
            </div>

            {/* Generate Error button
            <button
              className="btn btn-outline-danger waves-effect waves-light"
              onClick={() => {
                // Create a case with a duplicate ID to trigger an error
                setVisitData(prev => ({
                  ...prev,
                  CoreServer_CaseID: 'duplicate_case_id_for_testing',
                  patient_info: {
                    ...prev.patient_info,
                    patient_id: 'ERROR_TEST_PATIENT',
                    first_name: 'Error',
                    last_name: 'Test',
                    dob: '2000-01-01'
                  }
                }));
              }}
              disabled={isLoading}
              title="Sets up data that will likely cause an error when submitted"
            >
              <i className="fas fa-exclamation-triangle me-1"></i> Test Error Case
            </button> */}
          </div>
        </div>
      </div>
      
      {/* Domain Status section */}
      {successMessage && (
        <div className="alert alert-success mt-3">
          {successMessage}
          {generatedUrl && (
            <div className="mt-3">
              <strong>Patient Check-in URL:</strong> {generatedUrl}
              <button className="ms-2" onClick={() => handleCopy(generatedUrl, 'url')}><i className="fa fa-copy"></i></button>
              {copySuccessMap.url && <small className="ms-2">{copySuccessMap.url}</small>}
            </div>
          )}
           {responseDetails && (
            <div className="mt-2">
              <strong>Date of birth:</strong> {responseDetails.domain_status.patient.details.dob}
            </div>
          )}
           {generatedCaseId && (
            <div className="mt-2">
              <strong>MiraSol Case ID:</strong> {generatedCaseId}
              <button className="ms-2" onClick={() => handleCopy(generatedCaseId, 'caseId')}><i className="fa fa-copy"></i></button>
              {copySuccessMap.caseId && <small className="ms-2">{copySuccessMap.caseId}</small>}
            </div>
          )}
        </div>
      )}
      {domainStatus && (
        <div className="card border shadow-none mt-4">
          <div className="card-header bg-transparent border-bottom">
            <h5 className="mb-0"><i className="fas fa-tasks me-2"></i>Domain Operation Status</h5>
          </div>
          <div className="card-body">
            <div className="row">
              {Object.entries(domainStatus).map(([domain, details]) => (
                <div key={domain} className="col-md-6 mb-3">
                  <div className={`card ${details.status === 'success' ? 'border-success' :
                                         details.status === 'error' ? 'border-danger' :
                                         details.status === 'skipped' ? 'border-warning' : 'border-info'}`}>
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0">
                        {domain.charAt(0).toUpperCase() + domain.slice(1)}
                        <span className={`badge ms-2 ${
                          details.status === 'success' ? 'bg-success' :
                          details.status === 'error' ? 'bg-danger' :
                          details.status === 'skipped' ? 'bg-warning' :
                          details.status === 'in_progress' ? 'bg-info' : 'bg-secondary'
                        }`}>
                          {details.status}
                        </span>
                      </h6>
                    </div>
                    <div className="card-body">
                      <p><strong>Message:</strong> {details.message}</p>
                      {details.operation && <p><strong>Operation:</strong> {details.operation}</p>}
                      {details.id && <p><strong>ID:</strong> {details.id}</p>}
                      {domain === 'zoom_session' && (
                        <div>
                          <p><strong>Topic:</strong> {details.topic}
                          <button className="ms-2 me-2" onClick={() => handleCopy(details.topic, 'topic')}>
                            <i className="fa fa-copy"></i>
                          </button>
                          {copySuccessMap.topic && <small className="text-success">{copySuccessMap.topic}</small>}</p>
                          <p><strong>Passcode:</strong> {details.passcode}
                          <button className="ms-2 me-2" onClick={() => handleCopy(details.passcode, 'passcode')}>
                            <i className="fa fa-copy"></i>
                          </button>
                          {copySuccessMap.passcode && <small className="text-success">{copySuccessMap.passcode}</small>}</p>
                          <p><strong>Host Token:</strong> {details.host_token}</p>
                          <p><strong>Session ID:</strong> {details.session_id}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Form Assignment Results section */}
      {formAssignmentResults.length > 0 && (
        <div className="card border shadow-none mt-4">
          <div className="card-header bg-transparent border-bottom">
            <h5 className="mb-0"><i className="fas fa-clipboard-list me-2"></i>Form Assignment Details</h5>
          </div>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Requested Form</th>
                    <th>Status</th>
                    <th>Assigned Form ID</th>
                    <th>Assigned Version</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {formAssignmentResults.map((result, idx) => {
                    // Determine row styling based on backend flags
                    let rowClass = '';
                    let statusText = '';
                    
                    if (!result.assigned) {
                      rowClass = 'table-danger';
                      statusText = 'Failed';
                    } else if (result.version_reassigned) {
                      rowClass = 'table-warning'; // Orange/yellow styling for version reassignment
                      statusText = 'Assigned (Version modified)';
                    } else {
                      rowClass = 'table-success';
                      statusText = 'Success';
                    }
                    
                    return (
                      <tr key={idx} className={rowClass}>
                        <td>{JSON.stringify(result.requested_identifier)}</td>
                        <td>{statusText}</td>
                        <td>{result.assigned_form_id || 'N/A'}</td>
                        <td>
                          {result.version_reassigned && result.requested_version ? (
                            <span>
                              <strong>{result.assigned_version}</strong>
                              <small className="text-muted"> (requested: {result.requested_version})</small>
                            </span>
                          ) : (
                            result.assigned_version || 'N/A'
                          )}
                        </td>
                        <td>
                          {result.version_reassigned ? (
                            <span>
                              <strong>Form version modified:</strong> Requested version {result.requested_version} not found for this healthcare system. Assigned latest active version {result.assigned_version} instead.
                            </span>
                          ) : (
                            result.reason
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </div>
        </div>
      )}

    <Modal
      show={showModal}
      title={modalTitle}
      content={modalContent}
      onClose={() => setShowModal(false)}
      onCopy={handleCopy}
      copySuccessMap={copySuccessMap}
    />
      {/* Request and Response Payloads */}
      {(requestPayload || responseDetails) && (
        <div className="card border shadow-none mt-4">
          <div className="card-header bg-transparent border-bottom">
            <h5 className="mb-0"><i className="fas fa-exchange-alt me-2"></i>API Payloads</h5>
          </div>
          <div className="card-body">
            <div className="d-flex justify-content-around">
              <button
                type="button"
                className="btn btn-info me-2"
                onClick={() => {
                  setModalTitle('Request Payload');
                  setModalContent(JSON.stringify(requestPayload, null, 2));
                  setShowModal(true);
                }}
              >
                Show Request Payload
              </button>
              {responseDetails && (
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={() => {
                    setModalTitle('Response Details');
                    setModalContent(JSON.stringify(responseDetails, null, 2));
                    setShowModal(true);
                  }}
                >
                  Show Response Details
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InitiateVisitPage;